import { Platform } from '@angular/cdk/platform';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { Inject, Injectable } from '@angular/core';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateAdapter,
  NgxMatDateFormats,
  NgxMatNativeDateAdapter
} from '@angular-material-components/datetime-picker';

/**
 * App-wide adapter and formats for every <ngx-mat-datetime-picker>: the Streaming hearing
 * date/time, the dynamic form's Date & Time fields, and the form builder's default value.
 *
 * It exists because the library's native adapter formats through Intl, which renders a
 * locale-specific string like '8/3/2026, 3:03 PM'. These forms need the unambiguous
 * 'dd-MMM-yyyy HH:mm:ss' instead, matching the application's requested display format.
 */
export const APP_DATE_TIME_INPUT_FORMAT = 'app-date-time-input';

/**
 * Date-only counterpart, for fields that represent a calendar day. The picker's [hideTime]
 * removes the time spinners from the panel but not from the input text, so a date-only field
 * still needs a consistent display format.
 */
export const APP_DATE_INPUT_FORMAT = 'app-date-input';

const APP_DATE_PARSE_FORMAT = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
};

const APP_DATE_TIME_PARSE_FORMAT = {
  ...APP_DATE_PARSE_FORMAT,
  hour12: false,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
};

/** Calendar labels are the same either way: only the input text carries a time. */
const APP_CALENDAR_LABELS = {
  monthYearLabel: { year: 'numeric', month: 'short' },
  dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
  monthYearA11yLabel: { year: 'numeric', month: 'long' }
};

export const APP_DATE_TIME_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: APP_DATE_TIME_PARSE_FORMAT,
  },
  display: {
    ...APP_CALENDAR_LABELS,
    dateInput: APP_DATE_TIME_INPUT_FORMAT,
  }
};

export const APP_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: APP_DATE_PARSE_FORMAT,
  },
  display: {
    ...APP_CALENDAR_LABELS,
    dateInput: APP_DATE_INPUT_FORMAT,
  }
};

@Injectable()
export class AppDateTimeAdapter extends NgxMatNativeDateAdapter {
  constructor(@Inject(MAT_DATE_LOCALE) matDateLocale: string, platform: Platform) {
    super(matDateLocale, platform);
  }

  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === APP_DATE_INPUT_FORMAT) {
      return this.formatDate(date);
    }

    if (displayFormat === APP_DATE_TIME_INPUT_FORMAT) {
      return this.formatDateTime(date);
    }

    return super.format(date, displayFormat);
  }

  private formatDate(date: Date): string {
    return [
      this.padDateSegment(date.getDate()),
      this.getMonthShortName(date.getMonth()),
      date.getFullYear()
    ].join('-');
  }

  private formatDateTime(date: Date): string {
    return `${this.formatDate(date)} ${this.formatTime(date)}`;
  }

  private formatTime(date: Date): string {
    return [
      this.padDateSegment(date.getHours()),
      this.padDateSegment(date.getMinutes()),
      this.padDateSegment(date.getSeconds())
    ].join(':');
  }

  private getMonthShortName(monthIndex: number): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[monthIndex] ?? 'Jan';
  }

  private padDateSegment(value: number): string {
    return value.toString().padStart(2, '0');
  }
}

export const DATE_TIME_ADAPTER_PROVIDER = {
  provide: NgxMatDateAdapter,
  useClass: AppDateTimeAdapter
};

export const DATE_TIME_FORMAT_PROVIDER = {
  provide: NGX_MAT_DATE_FORMATS,
  useValue: APP_DATE_TIME_FORMATS
};

/**
 * Scoped override of the app-wide formats, applied through DateOnlyFormatDirective rather than
 * being imported directly: NgxMatDatetimeInput resolves NGX_MAT_DATE_FORMATS from its own
 * element injector, so a directive sitting on the same input can swap the format for that
 * input alone.
 */
export const DATE_ONLY_FORMAT_PROVIDER = {
  provide: NGX_MAT_DATE_FORMATS,
  useValue: APP_DATE_FORMATS
};
